import React from "react";

export default function Terms() {
  return (
    <main style={styles.main}>
      <header style={styles.header}>
        <h1 style={styles.h1}>Terms of Service</h1>
        <p style={styles.meta}>Last updated: 1/20/2026</p>
      </header>

      <section style={styles.section}>
        <p>
          Welcome to DAD PARTNERS! These Terms of Service ("Terms") govern your use
          of our website, applications, and services (collectively, the "Service").
          By accessing or using our Service, you agree to be bound by these Terms.
        </p>

        <p style={styles.callout}>
          <strong>Quick Summary:</strong> These terms establish a fair relationship
          between us and you. We provide a valuable service, and you agree to use it
          responsibly. We respect your rights and expect you to respect ours and
          other users&apos; rights.
        </p>
      </section>

      <section style={styles.section}>
        <h2 style={styles.h2}>1. Acceptance of Terms</h2>
        <p>
          By accessing, browsing, or using our Service, you acknowledge that you
          have read, understood, and agree to be bound by these Terms and our
          Privacy Policy. If you do not agree to these Terms, you may not use our
          Service.
        </p>
        <p>
          These Terms apply to all users, including visitors, registered users, and
          paying customers. If you are using our Service on behalf of an
          organization, you represent that you have the authority to bind that
          organization to these Terms.
        </p>
      </section>

      <section style={styles.section}>
        <h2 style={styles.h2}>2. Description of Service</h2>
        <p>
          DAD PARTNERS provides{" "}
          <em>
            [describe your specific service offering here - be detailed about what
            you provide, key features, and primary value proposition]
          </em>
          . Our Service includes:
        </p>
        <ul style={styles.ul}>
          <li>Access to our platform and tools</li>
          <li>Customer support services</li>
          <li>Regular updates and improvements</li>
          <li>Security features and data protection</li>
        </ul>
        <p>
          We may modify, update, or discontinue features of our Service at any time.
          We will provide reasonable notice of material changes that affect your
          use of the Service.
        </p>
      </section>

      <section style={styles.section}>
        <h2 style={styles.h2}>3. Account Registration and Security</h2>

        <h3 style={styles.h3}>Account Creation</h3>
        <p>To access certain features, you must create an account. You agree to:</p>
        <ul style={styles.ul}>
          <li>Provide accurate, current, and complete information</li>
          <li>Update your information to keep it accurate and current</li>
          <li>Choose a strong password and keep it confidential</li>
          <li>Notify us immediately of any unauthorized access</li>
        </ul>

        <h3 style={styles.h3}>Account Responsibility</h3>
        <p>You are responsible for all activities that occur under your account. You agree to:</p>
        <ul style={styles.ul}>
          <li>Not share your account credentials with others</li>
          <li>Not create multiple accounts to circumvent restrictions</li>
          <li>Not impersonate others or provide false identity information</li>
          <li>Maintain the security of your account credentials</li>
        </ul>
      </section>

      <section style={styles.section}>
        <h2 style={styles.h2}>4. Acceptable Use Policy</h2>
        <p>You agree to use our Service responsibly and in compliance with all applicable laws. You may NOT:</p>

        <h3 style={styles.h3}>Prohibited Activities</h3>
        <ul style={styles.ul}>
          <li><strong>Violate Laws:</strong> Use the Service for any illegal purpose or in violation of any laws</li>
          <li><strong>Harm Others:</strong> Harass, abuse, threaten, or intimidate other users</li>
          <li><strong>Infringe Rights:</strong> Violate intellectual property, privacy, or other rights of others</li>
          <li><strong>Spread Malware:</strong> Upload viruses, malware, or malicious code</li>
          <li><strong>Spam:</strong> Send unsolicited communications or engage in spam activities</li>
          <li><strong>Abuse System:</strong> Attempt to gain unauthorized access or disrupt our systems</li>
          <li><strong>Commercial Misuse:</strong> Use the Service for unauthorized commercial purposes</li>
          <li><strong>Data Mining:</strong> Scrape, crawl, or extract data without permission</li>
        </ul>

        <h3 style={styles.h3}>Content Standards</h3>
        <p>Any content you upload or share must not contain:</p>
        <ul style={styles.ul}>
          <li>Illegal, harmful, or offensive material</li>
          <li>Copyrighted material without permission</li>
          <li>Personal information of others without consent</li>
          <li>False, misleading, or defamatory information</li>
        </ul>
      </section>

      <section style={styles.section}>
        <h2 style={styles.h2}>5. Intellectual Property Rights</h2>

        <h3 style={styles.h3}>Our Rights</h3>
        <p>
          The Service and all related content, features, and functionality are owned
          by DAD PARTNERS and are protected by copyright, trademark, and other
          intellectual property laws. You may not:
        </p>
        <ul style={styles.ul}>
          <li>Copy, modify, or distribute our content without permission</li>
          <li>Reverse engineer or attempt to extract source code</li>
          <li>Remove or modify any proprietary notices</li>
          <li>Use our trademarks without authorization</li>
        </ul>

        <h3 style={styles.h3}>Your Rights</h3>
        <p>
          You retain ownership of content you create and upload to our Service. By
          using our Service, you grant us a non-exclusive, worldwide, royalty-free
          license to:
        </p>
        <ul style={styles.ul}>
          <li>Host, store, and backup your content</li>
          <li>Display your content as part of the Service</li>
          <li>Make technical modifications necessary for the Service</li>
          <li>Provide the Service to you and other users</li>
        </ul>
        <p>
          This license ends when you delete your content or terminate your account,
          except for content shared with other users.
        </p>
      </section>

      <section style={styles.section}>
        <h2 style={styles.h2}>6. Payment Terms</h2>

        <h3 style={styles.h3}>Pricing and Billing</h3>
        <p>Current pricing is available on our website. By subscribing to a paid plan:</p>
        <ul style={styles.ul}>
          <li>You agree to pay all applicable fees</li>
          <li>Payments are processed by secure third-party providers</li>
          <li>All fees are non-refundable unless otherwise stated</li>
          <li>We may change pricing with 30 days&apos; notice</li>
        </ul>

        <h3 style={styles.h3}>Automatic Renewal</h3>
        <p>
          Subscriptions automatically renew unless cancelled before the renewal
          date. You can cancel anytime through your account settings or by
          contacting us.
        </p>

        <h3 style={styles.h3}>Failed Payments</h3>
        <p>
          If payment fails, we may suspend your account until payment is resolved.
          We will notify you before any suspension.
        </p>
      </section>

      <section style={styles.section}>
        <h2 style={styles.h2}>7. Privacy and Data Protection</h2>
        <p>
          Your privacy is important to us. Our Privacy Policy explains how we
          collect, use, and protect your information. By using our Service, you
          also agree to our Privacy Policy.
        </p>
        <p>
          We implement appropriate security measures to protect your data, but you
          acknowledge that no method of transmission or storage is 100% secure.
        </p>
      </section>

      <section style={styles.section}>
        <h2 style={styles.h2}>8. Service Availability and Support</h2>

        <h3 style={styles.h3}>Service Availability</h3>
        <p>
          We strive to maintain high service availability but cannot guarantee
          uninterrupted access. We may perform maintenance that temporarily limits
          access, and we will provide reasonable notice when possible.
        </p>

        <h3 style={styles.h3}>Support</h3>
        <p>
          We provide customer support through{" "}
          <em>[specify your support channels - email, chat, phone, etc.]</em>.
          Support is available{" "}
          <em>[specify your support hours and response times]</em>.
        </p>
      </section>

      <section style={styles.section}>
        <h2 style={styles.h2}>9. Disclaimers and Limitation of Liability</h2>

        <h3 style={styles.h3}>Service Disclaimers</h3>
        <p>
          Our Service is provided &quot;as is&quot; and &quot;as available.&quot; We
          disclaim all warranties, express or implied, including:
        </p>
        <ul style={styles.ul}>
          <li>Warranties of merchantability and fitness for a particular purpose</li>
          <li>Warranties that the Service will be uninterrupted or error-free</li>
          <li>Warranties regarding the accuracy or reliability of content</li>
        </ul>

        <h3 style={styles.h3}>Limitation of Liability</h3>
        <p>
          To the maximum extent permitted by law, DAD PARTNERS shall not be liable
          for:
        </p>
        <ul style={styles.ul}>
          <li>Indirect, incidental, special, or consequential damages</li>
          <li>Loss of profits, data, or business opportunities</li>
          <li>Damages resulting from your use or inability to use the Service</li>
          <li>Third-party content or actions of other users</li>
        </ul>
        <p>
          Our total liability for any claim shall not exceed the amount you paid us
          in the 12 months preceding the claim.
        </p>
      </section>

      <section style={styles.section}>
        <h2 style={styles.h2}>10. Indemnification</h2>
        <p>
          You agree to defend, indemnify, and hold harmless DAD PARTNERS from any
          claims, damages, costs, and expenses (including legal fees) arising from:
        </p>
        <ul style={styles.ul}>
          <li>Your use of the Service</li>
          <li>Your violation of these Terms</li>
          <li>Your violation of any rights of others</li>
          <li>Content you upload or share through the Service</li>
        </ul>
      </section>

      <section style={styles.section}>
        <h2 style={styles.h2}>11. Termination</h2>

        <h3 style={styles.h3}>Termination by You</h3>
        <p>
          You may terminate your account at any time by contacting us or using
          account settings. Upon termination:
        </p>
        <ul style={styles.ul}>
          <li>Your access to the Service will cease</li>
          <li>Your data may be deleted after a reasonable period</li>
          <li>These Terms will no longer apply to future use</li>
        </ul>

        <h3 style={styles.h3}>Termination by Us</h3>
        <p>We may suspend or terminate your account if:</p>
        <ul style={styles.ul}>
          <li>You violate these Terms</li>
          <li>You fail to pay applicable fees</li>
          <li>We reasonably believe termination is necessary to protect our Service or other users</li>
          <li>Required by law or legal process</li>
        </ul>
        <p>
          We will provide reasonable notice before termination unless immediate
          termination is necessary for security or legal reasons.
        </p>
      </section>

      <section style={styles.section}>
        <h2 style={styles.h2}>12. Dispute Resolution</h2>

        <h3 style={styles.h3}>Governing Law</h3>
        <p>
          These Terms are governed by the laws of{" "}
          <em>[specify your jurisdiction]</em>. Any disputes will be resolved in the
          courts of <em>[specify your jurisdiction]</em>.
        </p>

        <h3 style={styles.h3}>Informal Resolution</h3>
        <p>
          Before filing any legal action, you agree to first contact us to attempt
          to resolve the dispute informally. We commit to working with you in good
          faith to resolve any issues.
        </p>

        <h3 style={styles.h3}>Arbitration</h3>
        <p>
          <em>
            [Optional: Include arbitration clause if preferred - this requires
            careful legal consideration for your jurisdiction]
          </em>
        </p>
      </section>

      <section style={styles.section}>
        <h2 style={styles.h2}>13. Changes to Terms</h2>
        <p>We may modify these Terms from time to time. When we make changes:</p>
        <ul style={styles.ul}>
          <li>We will notify you via email or prominent notice on our Service</li>
          <li>Changes become effective 30 days after notification</li>
          <li>Continued use of the Service constitutes acceptance of new Terms</li>
          <li>If you disagree with changes, you may terminate your account</li>
        </ul>
      </section>

      <section style={styles.section}>
        <h2 style={styles.h2}>14. General Provisions</h2>

        <h3 style={styles.h3}>Entire Agreement</h3>
        <p>
          These Terms, together with our Privacy Policy, constitute the entire
          agreement between you and DAD PARTNERS.
        </p>

        <h3 style={styles.h3}>Severability</h3>
        <p>
          If any provision of these Terms is found to be unenforceable, the
          remaining provisions will remain in full force and effect.
        </p>

        <h3 style={styles.h3}>Waiver</h3>
        <p>
          Our failure to enforce any provision does not waive our right to enforce
          it later.
        </p>

        <h3 style={styles.h3}>Assignment</h3>
        <p>
          You may not assign these Terms without our consent. We may assign these
          Terms without restriction.
        </p>

        <h3 style={styles.h3}>Force Majeure</h3>
        <p>
          We are not liable for delays or failures due to circumstances beyond our
          reasonable control, including natural disasters, pandemics, government
          actions, or infrastructure failures.
        </p>
      </section>

      <section style={styles.section}>
        <h2 style={styles.h2}>15. Contact Information</h2>
        <p>For questions about these Terms or to report violations:</p>
        <address style={styles.address}>
          <div>Email: <a href="mailto:DarbyEntLLC@gmail.com">DarbyEntLLC@gmail.com</a></div>
          <div style={{ marginTop: 8 }}>
            <strong>DAD PARTNERS</strong><br />
            Attention: Legal Department<br />
            124 Chapel Fields Ln<br />
            Garner, NC 27529<br />
            USA
          </div>
          <div style={{ marginTop: 8 }}>
            <strong>Response Time:</strong> We respond to inquiries within 3-5 business days.
          </div>
        </address>

        <p style={{ marginTop: 14, opacity: 0.85 }}>
          These Terms of Service are designed to be comprehensive while remaining fair and understandable.
          They protect both your rights as a user and our rights as a service provider.
        </p>
      </section>
    </main>
  );
}

const styles = {
  main: {
    maxWidth: 980,
    margin: "0 auto",
    padding: "32px 20px",
    lineHeight: 1.65,
  },
  header: {
    marginBottom: 18,
  },
  h1: {
    margin: "0 0 6px 0",
    fontSize: 36,
    letterSpacing: "-0.02em",
  },
  meta: {
    margin: 0,
    opacity: 0.75,
  },
  section: {
    marginTop: 22,
  },
  h2: {
    margin: "18px 0 8px",
    fontSize: 22,
    letterSpacing: "-0.01em",
  },
  h3: {
    margin: "14px 0 6px",
    fontSize: 16,
    textTransform: "none",
  },
  ul: {
    marginTop: 8,
    marginBottom: 0,
    paddingLeft: 22,
  },
  address: {
    marginTop: 10,
    padding: 12,
    border: "1px solid rgba(0,0,0,0.12)",
    borderRadius: 12,
    fontStyle: "normal",
  },
  callout: {
    marginTop: 12,
    padding: 12,
    border: "1px solid rgba(0,0,0,0.12)",
    borderRadius: 12,
    background: "rgba(0,0,0,0.03)",
  },
};
